import { Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class UnmaskAttributeService implements OnDestroy {
  private mutationObserver?: MutationObserver;
  private initialized = false;
  private initTimeoutHandle?: ReturnType<typeof setTimeout>;

  // Excludes hidden inputs and elements already processed to avoid redundant DOM writes
  private readonly selector =
    'input:not([type="hidden"]):not([data-di-unmask-field]), select:not([data-di-unmask-field])';

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Defer to next macrotask so Angular finishes initial rendering before we scan the DOM
    this.initTimeoutHandle = setTimeout(() => {
      if (!this.initialized) {
        return;
      }
      this.applyUnmaskToDocument();
      this.setupMutationObserver();
    }, 0);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  disconnect(): void {
    if (this.initTimeoutHandle !== undefined) {
      clearTimeout(this.initTimeoutHandle);
      this.initTimeoutHandle = undefined;
    }
    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
    this.initialized = false;
  }

  private setupMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') return;

    // Process synchronously — Medallia DXA caches the masking decision when an element
    // first appears; any async delay causes it to be recorded as masked permanently
    this.mutationObserver = new MutationObserver((mutations: MutationRecord[]) => {
      this.processAddedNodes(mutations);
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private processAddedNodes(mutations: MutationRecord[]): void {
    try {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement)) {
            continue;
          }
          if (node instanceof HTMLInputElement || node instanceof HTMLSelectElement) {
            this.setUnmaskAttribute(node);
          }
          // Scoped to the added subtree only — not a full-document scan
          const children = node.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
            this.selector,
          );
          for (const child of Array.from(children)) {
            this.setUnmaskAttribute(child);
          }
        }
      }
    } catch (error) {
      console.log('UnmaskAttributeService: Failed to apply unmask attributes', error);
    }
  }

  private applyUnmaskToDocument(): void {
    try {
      const elements = document.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
        this.selector,
      );
      for (const element of Array.from(elements)) {
        this.setUnmaskAttribute(element);
      }
    } catch (error) {
      console.log('UnmaskAttributeService: Failed to apply unmask attributes', error);
    }
  }

  private setUnmaskAttribute(element: HTMLInputElement | HTMLSelectElement): void {
    const type = element.getAttribute('type');
    if (type === 'hidden') {
      return;
    }
    // ??= preserves any existing value set manually in a template
    element.dataset.diUnmaskField ??= '';
  }
}
